import { useEffect, useMemo, useState } from 'react';
import { CircleNotch, PencilSimple } from '@phosphor-icons/react';
import { onboardingApi } from '../api';

/**
 * The Talent Profile as the participant has built it. Read-only, with a way
 * back into every section. Nothing derived or internal appears here — only
 * what they typed.
 */

const labelLookup = (options = []) =>
  options.reduce((acc, option) => ({ ...acc, [option.key]: option.label }), {});

function Block({ name, sectionKey, onEdit, children }) {
  return (
    <div className="ob-section-block">
      <div className="ob-section-head">
        <span className="ob-section-name">{name}</span>
        {sectionKey && (
          <button type="button" className="ob-edit-link" onClick={() => onEdit(sectionKey)}>
            <PencilSimple size={13} style={{ verticalAlign: -1, marginRight: 3 }} />
            Edit
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function KeyValues({ items }) {
  return (
    <div className="ob-kv">
      {items.map(({ key, value }) => (
        <div className="ob-kv-item" key={key}>
          <div className="ob-kv-key">{key}</div>
          <div className="ob-kv-val" data-empty={!value}>{value || 'Not provided'}</div>
        </div>
      ))}
    </div>
  );
}

function Pills({ values }) {
  if (!values.length) {
    return <div className="ob-kv-val" data-empty="true">Nothing selected</div>;
  }
  return (
    <div className="ob-tags">
      {values.map((text) => (
        <span key={text} className="ob-tag" data-selected="true" style={{ cursor: 'default' }}>
          {text}
        </span>
      ))}
    </div>
  );
}

export default function ReviewStep({ token, config, onEdit, refreshKey }) {
  const [state, setState] = useState({ loading: true, error: null, preview: null, outstanding: [] });

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));

    onboardingApi.preview(token)
      .then((response) => {
        if (cancelled) return;
        setState({
          loading: false,
          error: null,
          preview: response.preview,
          outstanding: response.outstanding || [],
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ loading: false, error: err.message, preview: null, outstanding: [] });
      });

    return () => { cancelled = true; };
  }, [token, refreshKey]);

  const lookups = useMemo(() => ({
    strengths: labelLookup(config.strengths),
    interests: labelLookup(config.interests),
    industries: labelLookup(config.industries),
    traits: labelLookup(config.perceptionTraits),
    priorities: labelLookup(config.learningPriorities),
    availability: labelLookup(config.availability),
    workModes: labelLookup(config.workModes),
    durations: labelLookup(config.durations),
    aiFamiliarity: labelLookup(config.aiFamiliarity),
    builderStatus: labelLookup(config.builderStatus),
    experienceTypes: labelLookup(config.experienceTypes),
  }), [config]);

  if (state.loading) {
    return (
      <section className="ob-card">
        <div style={{ display: 'grid', placeItems: 'center', padding: '48px 0', gap: 12 }}>
          <CircleNotch size={26} className="ob-spin" style={{ color: '#4f46e5' }} />
          <p className="ob-hint">Putting your profile together…</p>
        </div>
      </section>
    );
  }

  if (state.error) {
    return (
      <section className="ob-card">
        <h1 className="ob-h1">We could not load your profile</h1>
        <p className="ob-lede">{state.error}</p>
        <p className="ob-lede">Everything you wrote is still saved. Try refreshing the page.</p>
      </section>
    );
  }

  const p = state.preview;
  const map = (keys, lookup) => (keys || []).map((k) => lookup[k] || k);

  return (
    <section className="ob-card">
      <span className="ob-eyebrow">Almost there</span>
      <h1 className="ob-h1">Your Talent Profile</h1>
      <p className="ob-lede">
        This is what the programme team will see. Have a read through, change
        anything that does not feel right, then send it off.
      </p>

      {state.outstanding.length > 0 && (
        <div className="ob-outstanding" style={{ marginTop: 22 }}>
          <div className="ob-outstanding-title">A few sections still to finish</div>
          <div className="ob-outstanding-list">
            {state.outstanding.map((section) => (
              <button
                key={section.key}
                type="button"
                className="ob-outstanding-chip"
                onClick={() => onEdit(section.key)}
              >
                {section.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: 26 }}>
        <div className="ob-preview-head">
          {p.profile.photo_url && (
            <img src={p.profile.photo_url} alt="" className="ob-avatar" />
          )}
          <div style={{ minWidth: 0 }}>
            <h2 className="ob-preview-name">{p.profile.full_name || 'Your name'}</h2>
            <div className="ob-preview-meta">
              {[p.profile.city, p.profile.email].filter(Boolean).join(' · ')}
            </div>
          </div>
        </div>

        <Block name="About you" sectionKey="personal" onEdit={onEdit}>
          <KeyValues items={[
            { key: 'Full name', value: p.personal.full_name },
            { key: 'Father / guardian', value: p.personal.father_name },
            { key: 'Date of birth', value: p.personal.dob },
            { key: 'Nationality', value: p.personal.nationality },
            { key: 'Religion', value: p.personal.religion },
          ]} />
        </Block>

        <Block name="Contact" sectionKey="contact" onEdit={onEdit}>
          <KeyValues items={[
            { key: 'Phone', value: p.contact.phone },
            { key: 'Email', value: p.contact.email },
            { key: 'City', value: p.contact.city },
            { key: 'Address', value: p.contact.address },
            {
              key: 'Emergency contact',
              value: p.contact.emergency_name
                ? `${p.contact.emergency_name} (${p.contact.emergency_relation || '—'}) · ${p.contact.emergency_phone || ''}`
                : null,
            },
          ]} />
        </Block>

        <Block name="Education" sectionKey="education" onEdit={onEdit}>
          {p.education.length ? (
            <div className="ob-timeline">
              {p.education.map((entry, index) => (
                <div className="ob-timeline-item" key={index}>
                  <div className="ob-timeline-rail">
                    <span className="ob-timeline-dot" />
                    {index < p.education.length - 1 && <span className="ob-timeline-line" />}
                  </div>
                  <div className="ob-timeline-body">
                    <div className="ob-timeline-role">{entry.level}</div>
                    <div className="ob-timeline-org">{entry.institution}</div>
                    <div className="ob-timeline-when">
                      {entry.is_in_progress
                        ? 'In progress'
                        : [entry.passing_year, entry.grade_value].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="ob-kv-val" data-empty="true">Nothing added</div>
          )}
        </Block>

        <Block name="Skills" sectionKey="skills" onEdit={onEdit}>
          <Pills values={p.skills.map((s) =>
            `${s.skill_name}${s.is_currently_learning ? ' (learning)' : ''}`
          )} />
        </Block>

        <Block name="Experience" sectionKey="experience" onEdit={onEdit}>
          {p.experiences.length ? (
            <div className="ob-timeline">
              {p.experiences.map((entry, index) => (
                <div className="ob-timeline-item" key={index}>
                  <div className="ob-timeline-rail">
                    <span className="ob-timeline-dot" />
                    {index < p.experiences.length - 1 && <span className="ob-timeline-line" />}
                  </div>
                  <div className="ob-timeline-body">
                    <div className="ob-timeline-role">
                      {entry.role_title || lookups.experienceTypes[entry.experience_type]}
                    </div>
                    <div className="ob-timeline-org">{entry.organization}</div>
                    <div className="ob-timeline-when">
                      {entry.is_current ? 'Ongoing' : [entry.start_date, entry.end_date].filter(Boolean).join(' — ')}
                    </div>
                    {entry.description && <p className="ob-timeline-desc">{entry.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="ob-kv-val" data-empty="true">Skipped — no formal experience yet</div>
          )}
        </Block>

        <Block name="AI journey" sectionKey="ai_journey" onEdit={onEdit}>
          <KeyValues items={[
            { key: 'Where you are', value: lookups.aiFamiliarity[p.ai_journey.familiarity] },
            { key: 'Tools used', value: (p.ai_journey.tools || []).join(', ') },
          ]} />
          {p.ai_journey.story && (
            <p className="ob-timeline-desc" style={{ marginTop: 12 }}>{p.ai_journey.story}</p>
          )}
        </Block>

        <Block name="Natural strengths" sectionKey="strengths" onEdit={onEdit}>
          <Pills values={map(p.strengths, lookups.strengths)} />
        </Block>

        <Block name="Interests" sectionKey="interests" onEdit={onEdit}>
          <Pills values={map(p.interests, lookups.interests)} />
        </Block>

        <Block name="Where you’re headed" sectionKey="career_goals" onEdit={onEdit}>
          {p.career_goals.short_term && (
            <p className="ob-timeline-desc" style={{ marginTop: 0 }}>{p.career_goals.short_term}</p>
          )}
          {p.career_goals.long_term && (
            <p className="ob-timeline-desc">{p.career_goals.long_term}</p>
          )}
          <div style={{ marginTop: 12 }}>
            <Pills values={map(p.career_goals.learning_priorities, lookups.priorities)} />
          </div>
        </Block>

        <Block name="Industries" sectionKey="industries" onEdit={onEdit}>
          <Pills values={map(p.industries, lookups.industries)} />
        </Block>

        <Block name="Builder mindset" sectionKey="builder_mindset" onEdit={onEdit}>
          <KeyValues items={[
            { key: 'Have you built something', value: lookups.builderStatus[p.builder_mindset.status] },
          ]} />
          {p.builder_mindset.details && (
            <p className="ob-timeline-desc" style={{ marginTop: 12 }}>{p.builder_mindset.details}</p>
          )}
        </Block>

        <Block name="How others describe you" sectionKey="perception" onEdit={onEdit}>
          <Pills values={map(p.perception, lookups.traits)} />
        </Block>

        <Block name="Working with us" sectionKey="internship_prefs" onEdit={onEdit}>
          <KeyValues items={[
            { key: 'Availability', value: lookups.availability[p.internship_prefs.availability] },
            { key: 'Preferred mode', value: lookups.workModes[p.internship_prefs.work_mode] },
            { key: 'Shift', value: p.internship_prefs.preferred_shift },
            { key: 'Duration', value: lookups.durations[p.internship_prefs.duration] },
            { key: 'Earliest start', value: p.internship_prefs.earliest_start },
          ]} />
        </Block>

        <Block name="Your direction" sectionKey="department_prefs" onEdit={onEdit}>
          <div className="ob-rank">
            {p.department_preferences.map((pref) => (
              <div className="ob-rank-item" key={pref.department_key} data-top={pref.preference_rank === 1}>
                <span className="ob-rank-num">{pref.preference_rank}</span>
                <div className="ob-rank-text">
                  <div className="ob-rank-name">{pref.department?.name}</div>
                  {pref.reason && <div className="ob-rank-tag">{pref.reason}</div>}
                </div>
              </div>
            ))}
          </div>
        </Block>

        {p.final_reflection?.reflection && (
          <Block name="In your own words" sectionKey="final_reflection" onEdit={onEdit}>
            <p className="ob-timeline-desc" style={{ marginTop: 0 }}>
              {p.final_reflection.reflection}
            </p>
          </Block>
        )}
      </div>
    </section>
  );
}
